import { yupResolver } from "@hookform/resolvers/yup"
import { type Resolver, Controller, useForm } from "react-hook-form"
import { boolean, object, string } from "yup"
import { Button, Checkbox, Drawer, DrawerProps, Select } from "antd"
import { FormControl } from "@atoms"
import { useTranslation } from "react-i18next"
import { useAssignMonitor, useExtEmployees } from "./queries"
import { useEventId } from ".."
import { useCallback } from "react"
interface Props extends DrawerProps {
  onClose: () => void
  isOpen: boolean
  data?: unknown
}

const AssignMonitor: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const eventId = useEventId()
  const employees = useExtEmployees({
    enabled: props.isOpen,
  })
  const { isLoading, mutate } = useAssignMonitor(eventId!, {
    meta: {
      type: "assign",
      entity: t("monitors.monitor"),
    },
  })
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setError,
  } = useAssignMonitorForm({
    person_id: "",
    is_responsible: false,
    person_type: "employee",
    apply_to_similar_events: false,
  })

  const onClose = useCallback(() => {
    props.onClose()
    reset()
  }, [])

  const onSubmit = (data: IAssignMonitorForm) => {
    mutate(data, {
      onSuccess() {
        onClose()
      },
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (let key in error.response.data.errors) {
            setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  }

  return (
    <Drawer title={t("monitors.title")} {...{ ...props, onClose }}>
      <form>
        <FormControl>
          <Controller
            control={control}
            name="person_id"
            render={({ field }) => {
              return (
                <Select
                  loading={employees.isLoading}
                  options={employees.data?.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  showSearch
                  placeholder={t("global.select", {
                    entity: t("monitors.monitor"),
                    context: "male",
                    count: 1,
                    lettersCase: "capitalize",
                  })}
                  {...field}
                  value={field.value === "" ? undefined : field.value}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  status={errors.person_id && "error"}
                />
              )
            }}
          />
          {errors.person_id && (
            <FormControl.HelperText state="error">
              {errors.person_id.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <FormControl>
          <Controller
            control={control}
            name="is_responsible"
            render={({ field }) => {
              return (
                <Checkbox
                  {...field}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  {t("labels.responsable")}
                </Checkbox>
              )
            }}
          />
        </FormControl>
        <Button
          loading={isLoading}
          type="primary"
          onClick={handleSubmit(onSubmit)}
        >
          {t("global.save")}
        </Button>
      </form>
    </Drawer>
  )
}

export default AssignMonitor

export interface IAssignMonitorForm {
  person_id: string
  person_type: string
  is_responsible: boolean
  apply_to_similar_events: boolean
}

export function useAssignMonitorForm(defaultValues: IAssignMonitorForm) {
  const schema = object().shape({
    person_id: string().required(),
    is_responsible: boolean(),
  })
  return useForm({
    resolver: yupResolver(schema) as unknown as Resolver<IAssignMonitorForm>,
    defaultValues,
  })
}
